import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  XMarkIcon,
  MinusIcon,
  TrashIcon,
  ChevronUpIcon,
  PaperAirplaneIcon,
  ExclamationTriangleIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';
import { API_URL } from '../../services/api';
import {
  HELP_OFF_TOPIC_RESPONSE,
  HELP_UNAVAILABLE_RESPONSE,
  isSeoIntelligenceQuestion,
  parseHelpMessageSegments,
} from '../../utils/helpAssistant';
import { navActionButtonClass } from '../layout/navActionButton';

// Everyday questions any role is likely to ask — matches the live product.
const SUGGESTED_QUESTIONS = [
  'What can I do here?',
  'What is My Workspace?',
  'How do I update a task\'s status?',
  'What is Pending Work?',
  'How do I reset my password?',
  'How do I contact support?',
];

const CHAT_LIMIT = 50;

function HelpMessageContent({ content, variant = 'assistant', onNavigate }) {
  const navigate = useNavigate();
  const segments = parseHelpMessageSegments(content);
  const isUser = variant === 'user';

  if (segments.length === 1 && segments[0].type === 'text') {
    return <>{segments[0].value}</>;
  }

  return (
    <>
      {segments.map((seg, i) => {
        if (seg.type === 'text') return <span key={`t-${i}`}>{seg.value}</span>;
        if (seg.type === 'email') {
          return (
            <a
              key={`e-${i}`}
              href={`mailto:${seg.value}`}
              className={isUser ? 'font-semibold text-white underline' : 'font-semibold text-brand-600 hover:text-brand-700'}
            >
              {seg.value}
            </a>
          );
        }
        return (
          <button
            key={`l-${i}-${seg.path}`}
            type="button"
            onClick={() => {
              onNavigate?.(seg.path);
              navigate(seg.path);
            }}
            title={`Open ${seg.path}`}
            className={
              isUser
                ? 'inline font-semibold text-white no-underline hover:opacity-90'
                : 'inline font-semibold text-brand-600 no-underline hover:text-brand-700'
            }
          >
            {seg.label}
          </button>
        );
      })}
    </>
  );
}

function AssistantAvatar({ size = 14, className = 'text-white' }) {
  // Friendly chat-bubble-with-smile mark (outline, inherits color via currentColor).
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="M20.5 11.3a8.5 8.5 0 0 1-11.7 7.86L4 20.5l1.36-4.74A8.5 8.5 0 1 1 20.5 11.3z" />
      <path d="M8.75 10.75h.01" />
      <path d="M15.25 10.75h.01" />
      <path d="M9 13.75a3.25 3.25 0 0 0 6 0" />
    </svg>
  );
}

function TypingDots() {
  return (
    <div className="flex items-center gap-1 px-1 py-0.5">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="h-2 w-2 rounded-full bg-surface-400"
          style={{ animation: 'helpDotBounce 1.2s infinite ease-in-out', animationDelay: `${i * 0.18}s` }}
        />
      ))}
    </div>
  );
}

function MessageBubble({ msg, onNavigate }) {
  const isUser = msg.role === 'user';
  return (
    <div className={`flex w-full ${isUser ? 'justify-end' : 'justify-start'} mb-3`}>
      {!isUser && (
        <div className="mr-2 mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-brand-500 to-brand-600 shadow-sm">
          <AssistantAvatar size={15} />
        </div>
      )}
      <div
        className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed shadow-sm ${
          isUser
            ? 'rounded-tr-sm bg-gradient-to-br from-brand-500 to-brand-600 text-white'
            : 'rounded-tl-sm bg-white text-surface-700 ring-1 ring-surface-100'
        }`}
        style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
      >
        <HelpMessageContent content={msg.content} variant={isUser ? 'user' : 'assistant'} onNavigate={onNavigate} />
      </div>
    </div>
  );
}

export default function HelpAssistant() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);

  // Load saved conversation
  useEffect(() => {
    const saved = sessionStorage.getItem('helpAssistantMessages');
    if (saved) {
      try {
        setMessages(JSON.parse(saved));
      } catch (_) {
        // ignore parse errors
      }
    }
  }, []);

  // Persist conversation
  useEffect(() => {
    if (messages.length > 0) {
      sessionStorage.setItem('helpAssistantMessages', JSON.stringify(messages));
    }
  }, [messages]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading, scrollToBottom]);

  const handleClose = useCallback(() => {
    abortRef.current?.abort();
    setOpen(false);
    setMinimized(false);
  }, []);

  useEffect(() => {
    if (open && !minimized) {
      setTimeout(() => inputRef.current?.focus(), 120);
      const handleEscape = (e) => {
        if (e.key === 'Escape') handleClose();
      };
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [open, minimized, handleClose]);

  // External-open hook — any component can dispatch
  // window.dispatchEvent(new Event('helpAssistant:open')) to pop the chat open.
  useEffect(() => {
    const handler = () => {
      setOpen(true);
      setMinimized(false);
    };
    window.addEventListener('helpAssistant:open', handler);
    return () => window.removeEventListener('helpAssistant:open', handler);
  }, []);

  const chatTooLong = messages.length >= CHAT_LIMIT;

  const toggleHelp = () => {
    if (open) {
      if (minimized) setMinimized(false);
      else handleClose();
    } else {
      setOpen(true);
      setMinimized(false);
    }
  };

  const sendMessage = useCallback(
    async (text) => {
      const trimmed = text.trim();
      if (!trimmed || loading || chatTooLong) return;

      const userMsg = { id: `u-${Date.now()}`, role: 'user', content: trimmed };
      setMessages((prev) => [...prev, userMsg]);
      setInput('');
      setLoading(true);

      const assistantId = `a-${Date.now()}`;

      if (!isSeoIntelligenceQuestion(trimmed)) {
        setMessages((prev) => [...prev, { id: assistantId, role: 'assistant', content: HELP_OFF_TOPIC_RESPONSE }]);
        setLoading(false);
        return;
      }

      setMessages((prev) => [...prev, { id: assistantId, role: 'assistant', content: '' }]);

      const ctrl = new AbortController();
      abortRef.current = ctrl;

      try {
        const res = await fetch(`${API_URL}/help-assistant`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            messages: [...messages, userMsg].map((m) => ({ role: m.role, content: m.content })),
          }),
          signal: ctrl.signal,
        });

        if (!res.ok || !res.body) {
          let errText = HELP_UNAVAILABLE_RESPONSE;
          try {
            const raw = await res.text();
            if (raw.toLowerCase().includes('chat history too long')) {
              errText = 'Chat history too long. Please start a new conversation.';
            }
          } catch (_) {
            // ignore
          }
          setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, content: errText } : m)));
          return;
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let accumulated = '';

        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          accumulated += decoder.decode(value, { stream: true });
          const snap = accumulated;
          setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, content: snap } : m)));
        }

        if (!accumulated.trim()) {
          setMessages((prev) =>
            prev.map((m) => (m.id === assistantId ? { ...m, content: HELP_OFF_TOPIC_RESPONSE } : m))
          );
        }
      } catch (err) {
        if (err?.name !== 'AbortError') {
          setMessages((prev) =>
            prev.map((m) => (m.id === assistantId ? { ...m, content: HELP_UNAVAILABLE_RESPONSE } : m))
          );
        }
      } finally {
        setLoading(false);
        abortRef.current = null;
      }
    },
    [loading, messages, chatTooLong]
  );

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const handleClear = () => {
    abortRef.current?.abort();
    setMessages([]);
    setLoading(false);
    setInput('');
    sessionStorage.removeItem('helpAssistantMessages');
  };

  const handleHelpNavigate = useCallback(() => {
    setOpen(false);
    setMinimized(false);
  }, []);

  const showSuggestions = messages.length === 0 && !loading;

  return (
    <>
      <style>{`
        @keyframes helpDotBounce {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.5; }
          30% { transform: translateY(-5px); opacity: 1; }
        }
        @keyframes helpSlideUp {
          from { opacity: 0; transform: translateY(16px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        .help-slide-up { animation: helpSlideUp 0.22s cubic-bezier(0.16,1,0.3,1) both; }
      `}</style>

      {open && !minimized && (
        <div
          className="help-slide-up fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-[9999] flex flex-col overflow-hidden rounded-2xl bg-surface-50 shadow-[0_8px_40px_rgba(0,0,0,0.18)] ring-1 ring-surface-200 w-[calc(100vw-32px)] max-w-[370px] sm:w-[370px] max-h-[calc(100vh-120px)] sm:max-h-[540px]"
          role="dialog"
          aria-label="SEO Intelligence Assistant"
        >
          {/* Header */}
          <div className="flex shrink-0 items-center gap-3 bg-gradient-to-r from-brand-600 to-brand-500 px-4 py-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/20">
              <AssistantAvatar size={20} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-white">SEO Intelligence Assistant</p>
              <div className="flex items-center gap-1.5">
                <div className="h-1.5 w-1.5 rounded-full bg-green-300" />
                <p className="text-[11px] text-white/80">Online · Here to help</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {messages.length > 0 && (
                <button
                  onClick={handleClear}
                  title="Clear chat"
                  className="flex h-7 w-7 items-center justify-center rounded-lg text-white/70 transition-colors hover:bg-white/20 hover:text-white"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              )}
              <button
                onClick={() => setMinimized(true)}
                title="Minimize"
                className="flex h-7 w-7 items-center justify-center rounded-lg text-white/70 transition-colors hover:bg-white/20 hover:text-white"
              >
                <MinusIcon className="h-4 w-4" />
              </button>
              <button
                onClick={handleClose}
                title="Close"
                className="flex h-7 w-7 items-center justify-center rounded-lg text-white/70 transition-colors hover:bg-white/20 hover:text-white"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-2 pt-4">
            {messages.length === 0 && (
              <div className="mb-4 flex flex-col items-center text-center">
                <p className="text-sm font-semibold text-surface-800">Hi! I'm your SEO Intelligence Assistant</p>
                <p className="mt-1 text-xs text-surface-500">
                  Ask me about My Workspace, onboarding, tasks, tools, and settings.
                </p>
              </div>
            )}

            {showSuggestions && (
              <div className="mb-2">
                <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-surface-400">Quick questions</p>
                <div className="flex flex-wrap gap-1.5">
                  {SUGGESTED_QUESTIONS.map((q) => (
                    <button
                      key={q}
                      onClick={() => sendMessage(q)}
                      className="rounded-full border border-brand-200 bg-white px-3 py-1 text-xs text-brand-700 shadow-sm transition-all hover:border-brand-400 hover:bg-brand-50 hover:shadow"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg) => (
              <div key={msg.id}>
                {msg.content === '' && msg.role === 'assistant' && loading ? (
                  <div className="mb-3 flex items-start gap-2">
                    <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-brand-500 to-brand-600 shadow-sm">
                      <AssistantAvatar size={15} />
                    </div>
                    <div className="rounded-2xl rounded-tl-sm bg-white px-3.5 py-2.5 shadow-sm ring-1 ring-surface-100">
                      <TypingDots />
                    </div>
                  </div>
                ) : (
                  <MessageBubble msg={msg} onNavigate={handleHelpNavigate} />
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="shrink-0 border-t border-surface-200 bg-white px-3 pb-2.5 pt-2">
            {chatTooLong ? (
              <div className="flex flex-col items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-3 text-center">
                <ExclamationTriangleIcon className="h-5 w-5 text-amber-600" />
                <p className="text-xs font-medium text-amber-800">
                  Chat history too long. Please start a new conversation.
                </p>
                <button
                  onClick={handleClear}
                  className="rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-amber-700"
                >
                  Start new conversation
                </button>
              </div>
            ) : (
              <div className="flex items-end gap-2 rounded-xl border border-surface-200 bg-white px-2.5 py-1.5 transition-colors focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-500/10">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type your question here…"
                  rows={1}
                  disabled={loading}
                  className="min-h-[32px] flex-1 resize-none bg-transparent py-1 text-sm leading-5 text-surface-800 outline-none focus:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-surface-400 disabled:opacity-50"
                  style={{ maxHeight: 80, overflowY: 'auto' }}
                />
                <button
                  onClick={() => sendMessage(input)}
                  disabled={loading || !input.trim()}
                  title="Send"
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-brand-500 to-brand-600 text-white shadow-sm transition-all hover:shadow-md disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <PaperAirplaneIcon className="h-4 w-4" />
                </button>
              </div>
            )}
            {!chatTooLong && (
              <p className="mt-1 text-center text-[10px] leading-tight text-surface-400">
                Powered by SEO Intelligence AI · Press Enter to send
              </p>
            )}
          </div>
        </div>
      )}

      {open && minimized && (
        <button
          type="button"
          onClick={() => setMinimized(false)}
          className="help-slide-up fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-[9999] flex items-center gap-2.5 rounded-2xl bg-gradient-to-r from-brand-600 to-brand-500 px-4 py-2.5 text-white shadow-lg transition-all hover:shadow-xl"
        >
          <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-white/20">
            <AssistantAvatar size={14} />
          </div>
          <span className="text-sm font-medium">SEO Intelligence Assistant</span>
          <ChevronUpIcon className="h-4 w-4" />
        </button>
      )}

      <button
        type="button"
        onClick={toggleHelp}
        aria-label={open ? 'Close help assistant' : 'Open help assistant'}
        aria-pressed={open}
        title={open ? 'Close help assistant' : 'Ask the SEO Intelligence Assistant'}
        className={navActionButtonClass({ accent: true, active: open })}
      >
        <ChatBubbleLeftRightIcon className="h-4 w-4 shrink-0" strokeWidth={2} />
        <span>Help</span>
      </button>
    </>
  );
}
